import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/lib/auth/useAuth";
import { useTheme } from "@/lib/theme/ThemeProvider";
import { useNavigate } from "react-router-dom";
import { Sun, Moon, Monitor, LogOut, Globe, User, Settings } from "lucide-react";

export default function Header() {
  const { t, i18n } = useTranslation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { mode, setMode } = useTheme();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Close menu on outside click
  useEffect(() => {
    if (!menuOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [menuOpen]);

  const toggleLang = () => {
    i18n.changeLanguage(i18n.language === "es" ? "en" : "es");
  };

  const themeIcons = { light: Sun, dark: Moon, system: Monitor };
  const nextTheme: Record<string, "light" | "dark" | "system"> = {
    light: "dark",
    dark: "system",
    system: "light",
  };

  const ThemeIcon = themeIcons[mode];

  return (
    <header className="relative z-50 flex h-14 items-center justify-between border-b border-[var(--border)] bg-[var(--background)] px-4">
      <div className="flex items-center gap-3">
        <a href="/" className="flex items-center">
          <img
            src="/logo/sonia-by-fonestar-logotipo.svg"
            alt="Sonia by Fonestar"
            className="h-7"
          />
        </a>
      </div>

      <div className="flex items-center gap-2">
        <button
          onClick={toggleLang}
          className="flex items-center gap-1 rounded px-2 py-1 text-sm hover:bg-[var(--muted)]"
          title={i18n.language === "es" ? "English" : "Español"}
        >
          <Globe size={16} />
          {i18n.language.toUpperCase()}
        </button>

        <button
          onClick={() => setMode(nextTheme[mode])}
          className="rounded p-2 hover:bg-[var(--muted)]"
          title={t(`theme.${mode}`)}
        >
          <ThemeIcon size={16} />
        </button>

        {user && (
          <div ref={menuRef} className="relative">
            <button
              onClick={() => setMenuOpen(!menuOpen)}
              className="flex h-8 w-8 items-center justify-center overflow-hidden rounded-full transition-opacity hover:opacity-80"
            >
              {user.avatarUrl ? (
                <img
                  src={user.avatarUrl}
                  alt={user.name}
                  className="h-8 w-8 rounded-full object-cover"
                />
              ) : (
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-[var(--fonestar-gray-dark,#53565A)]">
                  <User className="h-4 w-4 text-white" />
                </div>
              )}
            </button>

            {menuOpen && (
              <div className="absolute right-0 top-full mt-1 w-48 rounded-lg border border-[var(--border)] bg-[var(--background)] py-1 shadow-lg">
                <div className="border-b border-[var(--border)] px-3 py-2">
                  <p className="text-sm font-medium">{user.name}</p>
                  <p className="text-xs text-[var(--muted-foreground)]">{user.email}</p>
                </div>
                {user.role === "admin" && (
                  <button
                    onClick={() => {
                      setMenuOpen(false);
                      navigate("/admin");
                    }}
                    className="flex w-full items-center gap-2 px-3 py-2 text-sm text-[var(--foreground)] transition-colors hover:bg-[var(--muted)]"
                  >
                    <Settings size={14} />
                    {t("admin.menu")}
                  </button>
                )}
                <button
                  onClick={() => {
                    setMenuOpen(false);
                    logout();
                  }}
                  className="flex w-full items-center gap-2 px-3 py-2 text-sm text-[var(--foreground)] transition-colors hover:bg-[var(--muted)]"
                >
                  <LogOut size={14} />
                  {t("auth.logout")}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </header>
  );
}
